import React from 'react';
import Grid from '@mui/material/Grid2';
import {
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import InputMask from 'react-input-mask';
import {
  INTERNAL_USER_FEATURES,
  PERMISSION_LEVELS,
} from './internalUserFormConfig';

export function getInternalUserEmailHelperText(emailChecking, emailExists) {
  if (emailChecking) {
    return 'Checking email...';
  }
  if (emailExists) {
    return 'This email already exists.';
  }
  return '';
}

export const renderInternalUserFeatureLabel = (mainText, description) => (
  <span>
    <Typography component="span" style={{ fontWeight: 'bold' }}>
      {mainText}
    </Typography>{' '}
    - {description}
  </span>
);

export const InternalUserEmailField = ({
  formData,
  onInputChange,
  emailExists,
  emailChecking,
  emailDialogOpen,
  onDialogClose,
  onDialogContinue,
  onDialogCancel,
}) => (
  <Grid size={{ xs: 12 }}>
    <TextField
      label="Email"
      fullWidth
      value={formData.email}
      onChange={onInputChange}
      name="email"
      variant="outlined"
      required
      type="email"
      error={Boolean(emailExists)}
      helperText={getInternalUserEmailHelperText(emailChecking, emailExists)}
    />
    <Dialog
      open={emailDialogOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onDialogClose();
      }}
    >
      <DialogTitle>Email Already Exists</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This email already exists. Would you like to continue with this email?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDialogCancel} color="secondary">
          Cancel
        </Button>
        <Button onClick={onDialogContinue} color="primary" autoFocus>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  </Grid>
);

export const InternalUserBasicFields = ({ formData, onInputChange }) => (
  <>
    <Grid size={{ xs: 12 }}>
      <TextField
        label="First Name"
        fullWidth
        value={formData.firstName}
        onChange={onInputChange}
        name="firstName"
        variant="outlined"
        required
        type="text"
      />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <TextField
        label="Last Name"
        fullWidth
        value={formData.lastName}
        onChange={onInputChange}
        name="lastName"
        variant="outlined"
        required
        type="text"
      />
    </Grid>
    <Grid size={{ xs: 12 }}>
      <InputMask
        mask="(999) 999-9999"
        value={formData.phoneNumber}
        onChange={onInputChange}
      >
        {(inputProps) => (
          <TextField
            {...inputProps}
            label="Phone Number"
            fullWidth
            name="phoneNumber"
            variant="outlined"
            required
          />
        )}
      </InputMask>
    </Grid>
  </>
);

export const InternalUserPermissionLevelField = ({ permissionLevel, onChange }) => (
  <Grid size={{ xs: 12 }}>
    <TextField
      label="Permission Level"
      fullWidth
      variant="outlined"
      select
      required
      value={permissionLevel}
      onChange={onChange}
    >
      {PERMISSION_LEVELS.map(({ value, label }) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  </Grid>
);

export const InternalUserAccessFlags = ({
  isCritical,
  isPiiUser,
  isCriticalUser,
  onPiiChange,
  onCriticalChange,
}) =>
  isCritical ? (
    <Grid size={{ xs: 12 }}>
      <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: '1rem' }}>
        User Access Flags
      </Typography>
      <Grid container spacing={2} direction="column" sx={{ mt: 0.5 }}>
        <Grid>
          <FormControlLabel
            control={
              <Checkbox checked={isPiiUser} onChange={(e) => onPiiChange(e.target.checked)} />
            }
            label={renderInternalUserFeatureLabel(
              'Has PII Access',
              'Grants access to view sensitive PII fields.'
            )}
          />
        </Grid>
        <Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCriticalUser}
                onChange={(e) => onCriticalChange(e.target.checked)}
              />
            }
            label={renderInternalUserFeatureLabel(
              'Critical User',
              'Gives priveldged access to the portal.'
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  ) : null;

export const InternalUserFeaturePermissions = ({
  permissionLevel,
  permissions,
  onPermissionChange,
}) =>
  permissionLevel === 'User' ? (
    <>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: '1rem' }}>
          Account Management Features
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2} direction="column">
          {INTERNAL_USER_FEATURES.map((feature) => (
            <Grid key={feature.value}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.includes(feature.value)}
                    onChange={onPermissionChange}
                    value={feature.value}
                  />
                }
                label={renderInternalUserFeatureLabel(
                  feature.label,
                  `Allows access to manage ${feature.label.toLowerCase()}.`
                )}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </>
  ) : null;
